/**
 * Website URL utility functions for locale-aware paths.
 * Following existing utility patterns (featureUrlService, workoutUrlUtils).
 */

import { i18nConfig, type LocaleCode } from '../config/i18n';
import { getSiteUrl } from '../config/website';

/** Query param used to bypass browser cache when switching locale (ensures fresh localized content) */
export const LOCALE_SWITCH_CACHE_BUST_PARAM = '_t';

/**
 * Returns search string with locale cache-bust param stripped.
 * Use when building locale-switch URLs to avoid _t accumulation.
 * Preserves other params (e.g. utm_*, mobileApp).
 * @param search - Raw search string (e.g. from window.location.search)
 * @returns Search string without _t, or empty string
 */
const TRACKING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Drops campaign parameters from marketing-site and app-store URLs.
 * Leaves team.hyperhuman.cc untouched so pricing attribution stays intact.
 */
export const stripMarketingTrackingParams = (url: string): string => {
  if (!url || !url.includes('utm_')) return url;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;
    const isStore = host === 'apps.apple.com' || host === 'play.google.com' || host.endsWith('.apple.com');
    const isMarketingSite = host === 'hyperhuman.cc' || host === 'www.hyperhuman.cc' || host === 'blog.hyperhuman.cc';
    if (!isStore && !isMarketingSite) return url;
    TRACKING_PARAMS.forEach((key) => parsed.searchParams.delete(key));
    return parsed.toString();
  } catch {
    return url;
  }
};

export const getSearchWithoutCacheBustParam = (search: string): string => {
  if (!search || !search.startsWith('?')) return '';
  const params = new URLSearchParams(search);
  params.delete(LOCALE_SWITCH_CACHE_BUST_PARAM);
  const filtered = params.toString();
  return filtered ? `?${filtered}` : '';
};

/**
 * Returns pathname with locale prefix for non-default locales.
 * Aligns with Gatsby trailingSlash: never - root returns /{path}, subpaths /{path}{basePath}.
 * @param pathname - Base path (e.g. /, /features, /about-us)
 * @param locale - Locale code
 * @returns Localized path
 */
export const getLocalizedPath = (pathname: string, locale: LocaleCode): string => {
  const config = i18nConfig[locale];
  if (!config || config.default) return pathname;
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (cleanPath === '/' || cleanPath === '') return `/${config.path}`;
  return `/${config.path}${cleanPath}`;
};

/**
 * Absolute localized homepage URL that opens support chat (`/?ask=1`).
 * A page-relative `?ask=1` would stay on the current path.
 */
export const getHomepageAskHref = (locale: LocaleCode): string => {
  const path = getLocalizedPath('/', locale);
  const origin = getSiteUrl().replace(/\/$/, '');
  if (path === '/' || path === '') return `${origin}/?ask=1`;
  return `${origin}${path}?ask=1`;
};

/**
 * Returns localized path for internal links, or original URL for external links.
 * Use for CMS-driven links (Prismic button_link, footer links, etc.).
 * @param link - Link from CMS (e.g. /features, https://example.com)
 * @param locale - Locale code
 * @returns Localized path or original URL
 */
export const getLocalizedLink = (link: string | undefined, locale: LocaleCode): string => {
  if (!link || link.startsWith('#') || link.includes('http')) return link || '/';
  const basePath = link.startsWith('/') ? link : `/${link}`;
  return getLocalizedPath(basePath, locale);
};

const TEAMS_CTA_HOST = 'team.hyperhuman.cc';

/** True when the URL host is the Teams app. Relative and invalid URLs are not. */
export const isTeamsCtaUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    return new URL(url).hostname === TEAMS_CTA_HOST;
  } catch {
    return false;
  }
};

/**
 * GTM reads `data-cta-location` from the anchor. Only Teams destinations get one.
 */
export const teamsCtaLocation = (
  url: string | undefined,
  location: string | undefined
): string | undefined => {
  if (!location || !isTeamsCtaUrl(url)) return undefined;
  return location;
};

/**
 * Merge the current page search into an absolute URL.
 * Keys already present on the destination are left as they are.
 * Returns `url` unchanged on the server, where `window` is unavailable.
 */
export const withCurrentSearch = (url: string): string => {
  if (typeof window === 'undefined') return url;
  const search = window.location.search;
  if (!search || search === '?') return url;
  try {
    const dest = new URL(url);
    const incoming = new URLSearchParams(search);
    incoming.forEach((value, key) => {
      if (!dest.searchParams.has(key)) {
        dest.searchParams.append(key, value);
      }
    });
    return dest.toString();
  } catch {
    return url;
  }
};

export const CONTACT_INQUIRY_PARAM = 'inquiry';
export const CONTACT_SOURCE_PARAM = 'source';

export const CONTACT_INQUIRY_VALUES = ['commercial', 'consulting', 'custom', 'partnerships', 'support'] as const;
export const CONTACT_SOURCE_VALUES = ['become-a-partner', 'resellers'] as const;

export type ContactInquiryValue = (typeof CONTACT_INQUIRY_VALUES)[number];
export type ContactSourceValue = (typeof CONTACT_SOURCE_VALUES)[number];

const isContactInquiryValue = (value: string | null): value is ContactInquiryValue =>
  value !== null && (CONTACT_INQUIRY_VALUES as readonly string[]).includes(value);

const isContactSourceValue = (value: string | null): value is ContactSourceValue =>
  value !== null && (CONTACT_SOURCE_VALUES as readonly string[]).includes(value);

/**
 * Reads allowlisted contact-form context from a search string.
 * Unknown inquiry/source values are ignored so translated labels and junk params
 * cannot prefill the form or leak into analytics.
 */
export const parseContactSearchParams = (
  search: string | undefined
): { inquiry?: ContactInquiryValue; source?: ContactSourceValue } => {
  try {
    const params = new URLSearchParams(search || '');
    const inquiryRaw = params.get(CONTACT_INQUIRY_PARAM);
    const sourceRaw = params.get(CONTACT_SOURCE_PARAM);
    return {
      ...(isContactInquiryValue(inquiryRaw) ? { inquiry: inquiryRaw } : {}),
      ...(isContactSourceValue(sourceRaw) ? { source: sourceRaw } : {}),
    };
  } catch {
    return {};
  }
};

/**
 * Locale-aware /contact link with optional inquiry prefill and GA source.
 * Uses stable option values (not labels) so the URL is locale-independent.
 */
export const getLocalizedContactLink = (
  locale: LocaleCode,
  options?: { inquiry?: ContactInquiryValue; source?: ContactSourceValue }
): string => {
  const params = new URLSearchParams();
  if (options?.inquiry && isContactInquiryValue(options.inquiry)) {
    params.set(CONTACT_INQUIRY_PARAM, options.inquiry);
  }
  if (options?.source && isContactSourceValue(options.source)) {
    params.set(CONTACT_SOURCE_PARAM, options.source);
  }
  const query = params.toString();
  return getLocalizedLink(query ? `/contact?${query}` : '/contact', locale);
};

/**
 * Strips locale prefix from pathname to get base path.
 * e.g. /en-gb/features -> /features, /features -> /features
 * @param pathname - Pathname that may include locale prefix
 * @returns Base path without locale
 */
export const getPathWithoutLocale = (pathname: string): string => {
  const [pathOnly] = pathname.split('?');
  const path = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const locales = Object.keys(i18nConfig) as LocaleCode[];
  for (const locale of locales) {
    const config = i18nConfig[locale];
    if (config && !config.default && path.startsWith(`/${config.path}/`)) {
      return path.slice(config.path.length + 1) || '/';
    }
    if (config && !config.default && path === `/${config.path}`) {
      return '/';
    }
  }
  return path;
};
